import React from "react";
import { Check } from "lucide-react";
import { clockStyleColorForInput } from "../../utils/clockWidgetStyle";

/**
 * @param {object} props
 * @param {string} props.variant
 * @param {Record<string, unknown>} props.style merged defaults + overrides
 * @param {(patch: Record<string, unknown>) => void} props.onPatch
 */
export default function ClockWidgetStylePanel({ variant, style, onPatch }) {
  const s = style || {};

  const rowColor = (label, key) => (
    <div className="de-rs-row" key={key} style={{ alignItems: "center", marginBottom: 8 }}>
      <label className="de-rs-inline-label" style={{ minWidth: 100, fontSize: 12 }}>
        {label}
      </label>
      <input
        type="color"
        value={clockStyleColorForInput(s[key], "#ffffff")}
        onChange={(e) => onPatch({ [key]: e.target.value })}
        style={{ width: 36, height: 28, border: "none", padding: 0, cursor: "pointer", borderRadius: 4 }}
      />
    </div>
  );

  if (variant === "gradient-night") {
    const grad = s.cardMode !== "solid";
    return (
      <div className="de-rs-section">
        <span className="de-rs-section-label">Clock appearance</span>
        <div className="de-rs-scale-mode" style={{ marginBottom: 10 }}>
          <button
            type="button"
            className={`de-rs-scale-btn ${grad ? "active" : ""}`}
            onClick={() => onPatch({ cardMode: "gradient" })}
          >
            {grad && <Check size={12} />} Gradient
          </button>
          <button
            type="button"
            className={`de-rs-scale-btn ${!grad ? "active" : ""}`}
            onClick={() => onPatch({ cardMode: "solid" })}
          >
            {!grad && <Check size={12} />} Solid
          </button>
        </div>
        {grad ? (
          <>
            {rowColor("Gradient A", "gradientColorLeft")}
            {rowColor("Gradient B", "gradientColorRight")}
          </>
        ) : (
          rowColor("Card", "cardSolid")
        )}
        {rowColor("Time", "timeColor")}
        {rowColor("AM/PM", "subColor")}
        {rowColor("Date", "dateColor")}
        {rowColor("Moon", "moonColor")}
      </div>
    );
  }

  if (variant === "liquid-crystal") {
    return (
      <div className="de-rs-section">
        <span className="de-rs-section-label">Clock appearance</span>
        {rowColor("Background", "lcdBg")}
        {rowColor("Digits", "lcdText")}
      </div>
    );
  }

  if (variant === "longform-analog") {
    return (
      <div className="de-rs-section">
        <span className="de-rs-section-label">Clock appearance</span>
        {rowColor("Face gradient A", "faceGradientA")}
        {rowColor("Face gradient B", "faceGradientB")}
        {rowColor("Outer ring", "faceStroke")}
        {rowColor("Hands and ring", "handColor")}
        {rowColor("Hour text", "hourTextColor")}
        {rowColor("Minute text", "minuteTextColor")}
        {rowColor("Calendar text", "calendarTextColor")}
        {rowColor("Dial fill", "dialFill")}
        {rowColor("Dial stroke", "dialStroke")}
      </div>
    );
  }

  if (variant === "neumorphic-analog") {
    return (
      <div className="de-rs-section">
        <span className="de-rs-section-label">Clock appearance</span>
        {rowColor("Frame", "frameBg")}
        {rowColor("Face outer", "faceOuter")}
        {rowColor("Face inner", "faceInner")}
        {rowColor("Inner ring", "faceInnerStroke")}
        {rowColor("Tick fill", "tickFill")}
        {rowColor("Tick stroke", "tickStroke")}
        {rowColor("Numbers", "digitColor")}
        <div className="de-rs-row" style={{ marginBottom: 8 }}>
          <div className="de-rs-field" style={{ flex: 1 }}>
            <label>Number size</label>
            <input
              type="number"
              min={10}
              max={40}
              value={Number(s.digitFontSize) || 24}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isFinite(v)) return;
                onPatch({ digitFontSize: Math.min(40, Math.max(10, v)) });
              }}
            />
          </div>
        </div>
        {rowColor("Hour hand", "handHour")}
        {rowColor("Minute hand", "handMinute")}
        {rowColor("Second hand", "handSecond")}
        {rowColor("Center cap", "pinOuter")}
        {rowColor("Center dot", "pinInner")}
      </div>
    );
  }

  return null;
}
